<script>
    import { createEventDispatcher } from 'svelte';
    import { fly, fade } from 'svelte/transition';
	import { DateInput } from 'date-picker-svelte'
    import { POCKETBASE_URL } from '../config';
    import Permisos from './CrudPermiso.svelte'; // Importamos el componente
    import Atributos from './CrudAtributo.svelte'; // Importamos el componente
    import Impuestos from './CrudImpuesto.svelte'; // Importamos el componente
    import { t } from 'svelte-i18n'
    import CrudGallery from './CrudGallery.svelte';
    import EmbeddedDetailTable from './EmbeddedDetailTable.svelte';

    export let newEntity; // Usar "entity" en lugar de "banco" para hacer reutilizable
    export let currentEntityId;
    export let isPaneOpen;
    export let togglePane;
    export let saveEntityEntry; // Función de guardado también genérica
    export let entityName = "Entidad"; // Nombre genérico de la entidad (se puede sobrescribir)
    export let editColumns;
    export let canCreate = false; // Nueva prop para crear
    export let canModify = false; // Nueva prop para modificar
    export let title = "Entity Management"; // Título genérico
    // Reusable Gallery options
    export let enableGallery = false;
    export let galleryConfig = {};
    // Detail configuration
    export let enableDetails = false;
    export let detailConfig = null;
    // Atributos configuration
    export let atributosConfig = null;
    // PDF generation configuration
    export let generateQuotationPDF = null; // Custom PDF generation function for quotations
    export let isGeneratingPDF = false; // Loading state for PDF generation
    export let handleSendEmail = null; // Custom email sending function
    export let isSendingEmail = false; // Loading state for email sending

    let permisos = []; // Nueva prop para gestionar permisos

    let activeTab = 'detalle';
    let errors = { nombre: '' }; // Validar el campo "nombre" genérico

    let isPreviewReady = true;

    const dispatch = createEventDispatcher();

    // Function to evaluate field conditions
    function evalCondition(condition) {
        if (!condition) return true;
        
        try {
            // Replace field references with actual values
            let evalCondition = condition;
            if (newEntity) {
                Object.keys(newEntity).forEach(key => {
                    const value = newEntity[key];
                    const regex = new RegExp(`\\b${key}\\b`, 'g');
                    evalCondition = evalCondition.replace(regex, `"${value}"`);
                });
            }
            
            // Evaluate the condition
            const result = eval(evalCondition);
            console.log('Condition evaluation:', condition, '->', evalCondition, '->', result);
            return result;
        } catch (error) {
            console.warn('Error evaluating condition:', condition, error);
            return true; // Show field by default if condition evaluation fails
        }
    }

    $: if (!isPaneOpen) {
        activeTab = 'detalle';
        resetErrors(); // Restablece errores al cerrar el panel
        resetForm(); // Reset form to default values
    }

    $: if (isPaneOpen && !currentEntityId) {
        // When opening pane for new entity, ensure default values are set
        resetForm();
    }

    // Reactive statement to handle form updates when tipo_dato changes
    $: if (newEntity && newEntity.tipo_dato) {
        // Force re-evaluation of conditions when tipo_dato changes
        console.log('tipo_dato changed to:', newEntity.tipo_dato);
    }

    $: if (newEntity) {
        formatEntity(editColumns); // Formatea la entidad solo cuando está disponible
    }

    function handleInput() {
        dispatch('inputChange');
    }

    function resetErrors() {
        errors = { nombre: '' }; // Reinicia errores
    }

    async function resetForm() {
        // Reset form to default values when pane is closed
        if (newEntity) {
            // Reset to default values based on the entity type
            if (entityName === 'cotiza_cab') {
                // Generate new quotation number
                try {
                    const { generateCotizaNumber } = await import('../controllers/cotizaCabController.js');
                    const numero = await generateCotizaNumber();
                    newEntity.numero = numero;
                } catch (error) {
                    console.error('Error generating cotiza number:', error);
                    // Use a fallback number based on timestamp
                    const timestamp = Date.now();
                    const lastDigits = timestamp.toString().slice(-6);
                    newEntity.numero = lastDigits.padStart(6, '0');
                }
                
                newEntity.fecha = new Date().toISOString().split('T')[0];
                newEntity.nit = '';
                newEntity.nombre = '';
                newEntity.telefono = '';
                newEntity.correo = '';
                newEntity.total = 0;
            }
            // Add other entity types as needed
        }
    }

    function validateFields() {
        let valid = true;

        // Special validation for password confirmation (only when creating new user)
        if (!currentEntityId && newEntity.password && newEntity.passwordConfirm && newEntity.password !== newEntity.passwordConfirm) {
            errors.passwordConfirm = 'Las contraseñas no coinciden.';
            valid = false;
        }

        editColumns.forEach(col => {
            const value = newEntity[col.key]; // Obtiene el valor del nuevo entidad

            // Skip password fields when editing existing user
            if (col.showOnCreate && currentEntityId) {
                return;
            }

            // Validar campos requeridos
            if (col.required) {
                if (col.type === 'text' || !col.type) {
                    // Verifica si el campo de texto está vacío
                    if (!value?.trim()) {
                        errors[col.key] = `${col.label} es requerido.`;
                        valid = false;
                    } else {
                        errors[col.key] = ''; // Reinicia el error si no está vacío
                    }
                } else if (col.type === 'number') {
                    // Verifica si el campo numérico es válido
                    if (value === undefined || value === null || isNaN(value)) {
                        errors[col.key] = `${col.label} debe ser un número válido.`;
                        valid = false;
                    } else {
                        errors[col.key] = ''; // Reinicia el error si es válido
                    }
                } else if (col.type === 'select') {
                    // Verifica si el campo de selección tiene un valor
                    if (!value) {
                        errors[col.key] = `${col.label} es requerido.`;
                        valid = false;
                    } else {
                        errors[col.key] = ''; // Reinicia el error si hay un valor
                    }
                } else if (col.type === 'date') {
                    if (!value || isNaN(new Date(value).getTime())) {
                        errors[col.key] = `${col.label} es requerido.`;
                        valid = false;
                    } else {
                        errors[col.key] = '';
                    }
                } else if (col.type === 'checkbox') {
                    // Verifica si el campo de selección tiene un valor
                    if (value === undefined) {
                        errors[col.key] = `${col.label} es requerido.`;
                        valid = false;
                    } else {
                        errors[col.key] = ''; // Reinicia el error si hay un valor
                    }
                } else if (col.type === 'email') {
                    // Verifica si el campo de texto es un correo electrónico válido
                    if (!value || !value.includes('@')) {
                        errors[col.key] = `${col.label} debe ser un correo electrónico válido.`;
                        valid = false;
                    } else {
                        errors[col.key] = ''; // Reinicia el error si es válido
                    }
                } else if (col.type === 'password') {
                    // Verifica si el campo de contraseña tiene un valor
                    if (!value || value.trim().length < 8) {
                        errors[col.key] = `${col.label} debe tener al menos 8 caracteres.`;
                        valid = false;
                    } else if (value.trim().length > 100) {
                        errors[col.key] = `${col.label} no puede tener más de 100 caracteres.`;
                        valid = false;
                    } else {
                        errors[col.key] = ''; // Reinicia el error si es válido
                    }
                } else if (col.type === 'file') {
                    // Verifica si el campo de archivo tiene un valor
                    if (!value) {
                        errors[col.key] = `${col.label} es requerido.`;
                        valid = false;
                    } else {
                        errors[col.key] = ''; // Reinicia el error si hay un valor
                    }
                } else if (col.type === 'json') {
                    // Verifica si el campo JSON tiene un valor
                    if (!value) {
                        errors[col.key] = `${col.label} es requerido.`;
                        valid = false;
                    } else {
                        errors[col.key] = ''; // Reinicia el error si hay un valor
                    }
                } else if (col.type === 'bool') {
                    // Verifica si el campo booleano tiene un valor
                    if (value === undefined) {
                        errors[col.key] = `${col.label} es requerido.`;
                        valid = false;
                    } else {
                        errors[col.key] = ''; // Reinicia el error si hay un valor
                    }
                } else if (col.type === 'multiselect') {
                    // Verifica si el campo multiselect tiene al menos un valor seleccionado
                    if (!value || !Array.isArray(value) || value.length === 0) {
                        errors[col.key] = `${col.label} es requerido.`;
                        valid = false;
                    } else {
                        errors[col.key] = ''; // Reinicia el error si hay valores seleccionados
                    }
                } else if (col.type === 'hidden') {
                    // No se valida el campo oculto
                    errors[col.key] = '';
                } else {
                    console.warn(`Tipo de campo no manejado: ${col.type}`);
                }
            }
        });

        return valid; // Retorna el estado de validez
    }

    async function handleSave() {
        if (validateFields()) {
            try {
                await saveEntityEntry(); // Llama la función de guardado si pasa la validación
                isPreviewReady = true;
                // Reset form after successful save
                resetForm();
            } catch (error) {
                console.error('Error saving entity:', error);
            }
        }
    }

    // Wrapper function for saving without closing pane (used by EmbeddedDetailTable)
    async function saveEntityWithoutClosingPane() {
        if (validateFields()) {
            try {
                const result = await saveEntityEntry(false); // Don't close pane
                isPreviewReady = true;
                // Don't reset form when auto-saving from detail table
                return result; // Return the result so EmbeddedDetailTable can get the ID
            } catch (error) {
                console.error('Error saving entity:', error);
                throw error; // Re-throw so EmbeddedDetailTable can handle it
            }
        }
    }

    // Revisión de fechas antes de montar
    function formatEntity(editColumns) {
        editColumns.forEach(col => {
            if (col.type === 'date') {
                if (newEntity[col.key] && typeof newEntity[col.key] === 'string') {
                    newEntity[col.key] = new Date(newEntity[col.key]);  // Asegura que sea un objeto Date
                }
            }
        });
    }

    // Store file preview URLs for File objects
    let filePreviews = {};

    // Manejar el cambio de archivo
    function handleFileUpload(event, key) {
        isPreviewReady = false;
        const file = event.target.files[0]; // Capturar el archivo
        if (file) {
            newEntity[key] = file;
            
            // Create preview URL for File object
            const reader = new FileReader();
            reader.onload = (e) => {
                filePreviews = { ...filePreviews, [key]: e.target.result };
                isPreviewReady = true;
            };
            reader.onerror = () => {
                isPreviewReady = true; // Still set to true even on error
            };
            reader.readAsDataURL(file);
        }
    }
    
    // Get preview URL for file field - handles both File objects and string filenames
    function getFilePreviewUrl(key) {
        const value = newEntity[key];
        if (!value) return null;
        
        // If it's a File object, use the preview URL from filePreviews
        if (value instanceof File) {
            return filePreviews[key] || null;
        }
        
        // If it's a string (existing file from PocketBase), construct the URL
        if (typeof value === 'string' && newEntity.collectionId && newEntity.id) {
            return POCKETBASE_URL + '/api/files/' + newEntity.collectionId + '/' + newEntity.id + '/' + value;
        }
        
        return null;
    }

    // Funciones para manejar multiselect
    function addSelectedItem(key, selectedValue) {
        if (!selectedValue) return;
        
        // Asegurar que el array existe
        if (!newEntity[key]) {
            newEntity[key] = [];
        }
        
        // Buscar la opción seleccionada
        const column = editColumns.find(col => col.key === key);
        const selectedOption = column.options.find(option => option.key === selectedValue);
        
        if (selectedOption && !isItemSelected(key, selectedValue)) {
            newEntity[key] = [...newEntity[key], selectedOption];
            handleInput();
        }
    }

    function removeSelectedItem(key, itemToRemove) {
        if (!newEntity[key]) return;
        
        newEntity[key] = newEntity[key].filter(item => item.key !== itemToRemove.key);
        handleInput();
    }

    function isItemSelected(key, itemKey) {
        if (!newEntity[key]) return false;
        return newEntity[key].some(item => item.key === itemKey);
    }
</script>

{#if isPaneOpen}
    <div class="fixed inset-0 z-40 bg-black bg-opacity-40" on:click|self={togglePane} on:keydown|self={(e) => e.key === 'Escape' && togglePane()} tabindex="-1" aria-modal="true" role="dialog" transition:fade> <!-- Backdrop -->
      <div
        transition:fly={{ x: 600, duration: 250 }}
        class="fixed top-0 right-0 h-full w-screen md:w-[60vw] md:min-w-[60vw] md:max-w-[60vw] box-border bg-white shadow-2xl text-base z-50 py-6 px-8 border-l border-blueGray-200 rounded-l-lg overflow-y-auto"
        aria-label="CRUD side panel"
      >
          <button class="absolute top-3 right-3 text-gray-600 hover:text-red-600 transition" on:click={togglePane}>
              <i class="fas fa-times"></i>
          </button>
          <br>
          <h2 class="text-xl font-bold">{currentEntityId ? $t('crudPane.editTitle', { values: { title } }) : $t('crudPane.createTitle', { values: { title } })}</h2>
            <!-- Pestañas -->
            <div class="flex justify-end space-x-4 mb-4 bg-gray-100 p-2 rounded">
                <button on:click={() => (activeTab = 'detalle')} class="px-4 py-2 border-b-2 {activeTab === 'detalle' ? 'font-bold bg-blue-100' : 'bg-white'}" class:font-bold={activeTab === 'detalle'}>
                    {$t('crudPane.tabs.detail')}
                </button>
                {#if enableDetails && detailConfig}
                    <button 
                        on:click={() => (activeTab = 'details')} 
                        class="px-4 py-2 border-b-2 {activeTab === 'details' ? 'font-bold bg-green-100' : 'bg-white'}">
                        {detailConfig.tabLabel || 'Details'}
                    </button>
                {/if}
                {#if newEntity['atributos'] || atributosConfig}
                    <button 
                        on:click={() => (activeTab = 'atributos')} 
                        class="px-4 py-2 border-b-2 {activeTab === 'atributos' ? 'font-bold' : ''}">
                        {$t('crudPane.tabs.attributes')}
                    </button>
                {/if}
                {#if enableGallery && galleryConfig && galleryConfig.collection}
                    <button 
                        on:click={() => (activeTab = 'galeria')} 
                        class="px-4 py-2 border-b-2 {activeTab === 'galeria' ? 'font-bold bg-purple-100' : 'bg-white'}">
                        {galleryConfig.tabLabel || $t('gallery.gallery')}
                    </button>
                {/if}
                {#if newEntity['impuestos']}
                    <button 
                        on:click={() => (activeTab = 'impuestos')} 
                        class="px-4 py-2 border-b-2 {activeTab === 'impuestos' ? 'font-bold' : ''}">
                        {$t('crudPane.tabs.taxes')}
                    </button>
                {/if}
            </div>
            <!-- Contenido -->
            <div class="grid grid-cols-1 gap-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {#if activeTab === 'detalle'}
                        {#each editColumns as column}
                            {#if column.type === "hidden"} <!-- Verifica si el campo no está oculto -->
                                <input 
                                    type="hidden" 
                                    bind:value={newEntity[column.key]} 
                                    on:input={handleInput} 
                                    disabled={!canModify && (!canCreate && currentEntityId) }
                                />
                            {/if}
                            {#if column.type !== "hidden" && (!column.showOnCreate || !currentEntityId) && (!column.condition || evalCondition(column.condition))}
                                <div class="space-y-1"> <!-- Añade separación vertical entre el label y el input -->
                                    <label for={column.key} class="block text-sm font-medium">{column.label}</label>

                                    {#if column.type === 'text'}
                                        <input
                                            type="text"
                                            placeholder={column.label}
                                            bind:value={newEntity[column.key]}
                                            on:input={handleInput}
                                            class="block w-full p-2 border border-gray-300 rounded-md text-sm"
                                            disabled={!canModify && (!canCreate || currentEntityId) }
                                        />
                                    {/if}

                                    {#if column.type === 'email'}
                                    <input
                                        type="email"
                                        placeholder={column.label}
                                        bind:value={newEntity[column.key]}
                                        on:input={handleInput}
                                        class="block w-full p-2 border border-gray-300 rounded-md text-sm"
                                        disabled={!canModify && (!canCreate || currentEntityId) }
                                        />
                                    {/if}

                                    {#if column.type === 'password'}
                                    <input
                                        type="password"
                                        placeholder={column.label}
                                        bind:value={newEntity[column.key]}
                                        on:input={handleInput}
                                        class="block w-full p-2 border border-gray-300 rounded-md text-sm"
                                        disabled={!canModify && (!canCreate || currentEntityId) }
                                        minlength="8"
                                        maxlength="100"
                                        />
                                    <p class="text-xs text-gray-500 mt-1">Mínimo 8 caracteres</p>
                                    {/if}

                                    {#if column.type === 'number'}
                                    <input
                                        type="number"
                                        placeholder={column.label}
                                        class="block w-full p-2 border border-gray-300 rounded-md text-sm"
                                        bind:value={newEntity[column.key]}
                                        on:input={handleInput}
                                        disabled={!canModify && (!canCreate || currentEntityId) }
                                        />
                                    {/if}

                                    {#if column.type === 'boolean'}
                                    <div class="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            bind:checked={newEntity[column.key]}
                                            on:change={handleInput}
                                            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            disabled={!canModify && (!canCreate || currentEntityId)}
                                            id="checkbox-{column.key}"
                                        />
                                        <label for="checkbox-{column.key}" class="text-sm text-gray-700">{column.label}</label>
                                    </div>
                                    {/if}

                                    {#if column.type === 'select' || column.type === 'json'}
                                        <select
                                            bind:value={newEntity[column.key]}
                                            class="block w-full p-2 border border-gray-300 rounded-md text-sm"
                                            on:blur={handleInput}
                                            disabled={!canModify && (!canCreate || currentEntityId)}
                                        >
                                            <option value="">{$t('common.selectOption')}</option>
                                            {#each column.options as option}
                                                <option value={option.key}>{option.value}</option>
                                            {/each}
                                        </select>
                                    {/if}

                                    {#if column.type === 'multiselect'}
                                        <div class="multiselect-container">
                                            <div class="selected-items mb-2">
                                                {#if newEntity[column.key] && newEntity[column.key].length > 0}
                                                    {#each newEntity[column.key] as selectedItem}
                                                        <span class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 mr-1 mb-1">
                                                            {selectedItem.value || selectedItem.descrip || selectedItem.nombre}
                                                            <button 
                                                                type="button"
                                                                class="ml-1 text-blue-600 hover:text-blue-800"
                                                                on:click={() => removeSelectedItem(column.key, selectedItem)}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    {/each}
                                                {:else}
                                                    <span class="text-gray-500 text-sm">No hay elementos seleccionados</span>
                                                {/if}
                                            </div>
                                            <select
                                                class="block w-full p-2 border border-gray-300 rounded-md text-sm"
                                                on:change={(e) => addSelectedItem(column.key, e.target.value)}
                                                disabled={!canModify && (!canCreate || currentEntityId)}
                                            >
                                                <option value="">Seleccionar categoría...</option>
                                                {#each column.options as option}
                                                    {#if !isItemSelected(column.key, option.key)}
                                                        <option value={option.key}>{option.value}</option>
                                                    {/if}
                                                {/each}
                                            </select>
                                        </div>
                                    {/if}

                                    {#if column.type === 'date'}
                                    <DateInput
                                        bind:value={newEntity[column.key]}
                                        class="block w-full p-2 border border-gray-300 rounded-md text-sm"
                                        on:input={handleInput}
                                        placeholder={$t('common.selectDate')}
                                        format="dd-MM-yyyy"
                                        disabled={!canModify && (!canCreate || currentEntityId) }
                                        />
                                    {/if}

                                    {#if column.type === 'checkbox'}
                                        <div class="flex items-center">
                                            <input
                                                type="checkbox"
                                                bind:checked={newEntity[column.key]}
                                                id={column.key}
                                                class="mr-2"
                                                on:change={handleInput}
                                                disabled={!canModify && (!canCreate || currentEntityId) }
                                                />
                                            <label for={column.key} class="text-sm">{column.label}</label>
                                        </div>
                                    {/if}

                                    {#if column.type === 'file'}
                                    <div class="items-center">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            on:change={(e) => handleFileUpload(e, column.key)}
                                            class="block w-full p-2 border border-gray-300 rounded-md text-sm"
                                            disabled={!canModify && (!canCreate || currentEntityId) }
                                        />
                                        {#if newEntity[column.key]}
                                            {#if getFilePreviewUrl(column.key)}
                                            <img
                                                src={getFilePreviewUrl(column.key)}
                                                alt="Preview"
                                                class="mt-2 w-24 h-24 object-cover"
                                            />
                                            {/if}
                                        {/if}
                                    </div>
                                    {/if}

                                    {#if errors[column.key]}
                                    <p class="text-red-500 text-sm">{errors[column.key]}</p>
                                    {/if}
                                </div>
                            {/if}
                        {/each}
                    {/if}
                    
                <!-- PDF and Email Actions for Quotations - Only show on Detail tab -->
                {#if entityName === 'cotiza_cab' && generateQuotationPDF && currentEntityId && activeTab === 'detalle'}
                    <div class="col-span-2 mt-8">
                        <div class="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <!-- Header -->
                            <div class="px-6 py-4 border-b border-gray-100">
                                <div class="flex items-center">
                                    <div class="flex-shrink-0">
                                        <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                            <i class="fas fa-bolt text-gray-600 text-sm"></i>
                                        </div>
                                    </div>
                                    <div class="ml-3">
                                        <h3 class="text-base font-semibold text-gray-900">{$t('quotation.tabs.actions')}</h3>
                                        <p class="text-sm text-gray-500">{$t('crudPane.quickActions')}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Actions -->
                            <div class="p-6">
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <!-- PDF Generation -->
                                    <div class="group relative">
                                        <div class="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200 hover:border-red-300 transition-all duration-200 hover:shadow-md">
                                            <div class="flex items-start space-x-3">
                                                <div class="flex-shrink-0">
                                                    <div class="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200">
                                                        <i class="fas fa-file-pdf text-white text-sm"></i>
                                                    </div>
                                                </div>
                                                <div class="flex-1 min-w-0">
                                                    <h4 class="text-sm font-semibold text-gray-900 mb-1">{$t('actions.generateQuotationPDF')}</h4>
                                                    <p class="text-xs text-gray-600 mb-3">{$t('actions.downloadProfessionalPDF')}</p>
                                                    <button 
                                                        on:click={() => generateQuotationPDF(newEntity)} 
                                                        disabled={isGeneratingPDF}
                                                        class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                                                        {#if isGeneratingPDF}
                                                            <i class="fas fa-spinner fa-spin mr-1.5"></i>
                                                            {$t('actions.generating')}
                                                        {:else}
                                                            <i class="fas fa-download mr-1.5"></i>
                                                            {$t('actions.generate')}
                                                        {/if}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- Email -->
                                    <div class="group relative">
                                        <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200 hover:border-blue-300 transition-all duration-200 hover:shadow-md">
                                            <div class="flex items-start space-x-3">
                                                <div class="flex-shrink-0">
                                                    <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200">
                                                        <i class="fas fa-envelope text-white text-sm"></i>
                                                    </div>
                                                </div>
                                                <div class="flex-1 min-w-0">
                                                    <h4 class="text-sm font-semibold text-gray-900 mb-1">{$t('actions.sendEmail')}</h4>
                                                    <p class="text-xs text-gray-600 mb-3">{$t('actions.emailQuotationToClient')}</p>
                                                    <button 
                                                        on:click={() => handleSendEmail(newEntity)} 
                                                        disabled={isSendingEmail}
                                                        class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                                                        {#if isSendingEmail}
                                                            <i class="fas fa-spinner fa-spin mr-1.5"></i>
                                                            {$t('actions.sending')}
                                                        {:else}
                                                            <i class="fas fa-paper-plane mr-1.5"></i>
                                                            {$t('actions.send')}
                                                        {/if}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                {/if}
                </div>
                <!-- Contenido de atributos -->
                {#if activeTab === 'atributos'}
                    {#if atributosConfig}
                        <div class="mt-4">
                            <EmbeddedDetailTable
                                parentEntityId={currentEntityId}
                                parentEntity={newEntity}
                                saveParentEntity={saveEntityWithoutClosingPane}
                                collection={atributosConfig.collection}
                                relationField={atributosConfig.relationField}
                                columns={atributosConfig.columns || []}
                                editColumns={atributosConfig.editColumns || []}
                                fetchDetails={atributosConfig.fetchAtributos}
                                saveDetail={atributosConfig.saveAtributo}
                                removeDetail={atributosConfig.removeAtributo}
                                searchDetails={atributosConfig.searchAtributos}
                                subscribeToDetails={atributosConfig.subscribeToAtributos}
                                unsubscribeFromDetails={atributosConfig.unsubscribeFromAtributos}
                                on:detailSaved={() => {
                                    // Refresh parent entity if needed
                                    console.log('Atributo saved');
                                }}
                                on:detailRemoved={() => {
                                    // Refresh parent entity if needed
                                    console.log('Atributo removed');
                                }}
                            />
                        </div>
                    {:else}
                        <Atributos 
                            newEntity={newEntity} 
                            entityName={entityName} 
                            canModify={canModify}
                            canCreate={canCreate}
                            currentEntityId={currentEntityId}
                        />
                    {/if}
                {/if}
                {#if activeTab === 'galeria' && enableGallery && galleryConfig && galleryConfig.collection}
                    <CrudGallery 
                        newEntity={newEntity}
                        entityName={entityName}
                        canModify={canModify}
                        canCreate={canCreate}
                        currentEntityId={currentEntityId}
                        galleryCollection={galleryConfig.collection}
                        relationField={galleryConfig.relationField}
                        urlField={galleryConfig.urlField}
                        fileField={galleryConfig.fileField}
                    />
                {/if}
                {#if activeTab === 'details' && enableDetails && detailConfig}
                    <div class="mt-4">
                        <EmbeddedDetailTable
                            parentEntityId={currentEntityId}
                            parentEntity={newEntity}
                            saveParentEntity={saveEntityWithoutClosingPane}
                            collection={detailConfig.collection}
                            relationField={detailConfig.relationField}
                            columns={detailConfig.columns || []}
                            editColumns={detailConfig.editColumns || []}
                            fetchDetails={detailConfig.fetchDetails}
                            saveDetail={detailConfig.saveDetail}
                            removeDetail={detailConfig.removeDetail}
                            searchDetails={detailConfig.searchDetails}
                            subscribeToDetails={detailConfig.subscribeToDetails}
                            unsubscribeFromDetails={detailConfig.unsubscribeFromDetails}
                            on:detailSaved={() => {
                                // Refresh parent entity if needed
                                if (detailConfig.onDetailSaved) {
                                    detailConfig.onDetailSaved();
                                }
                            }}
                            on:detailRemoved={() => {
                                // Refresh parent entity if needed
                                if (detailConfig.onDetailRemoved) {
                                    detailConfig.onDetailRemoved();
                                }
                            }}
                        />
                    </div>
                {/if}
                <!-- Contenido de impuestos -->
                {#if activeTab === 'impuestos'}
                    <Impuestos
                        newEntity={newEntity} 
                        entityName={entityName} 
                        canModify={canModify}
                        canCreate={canCreate}
                        currentEntityId={currentEntityId}
                    />
                {/if}
            </div>
            {#if canModify || canCreate}
                <button on:click={handleSave} class="mt-4 px-4 py-2 bg-blueGray-600 text-white rounded hover:bg-indigo-600">
                    {currentEntityId ? $t('common.update') : $t('common.create')}
                </button>
            {/if}
            <button on:click={togglePane} class="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">{$t('common.close')}</button>
        </div>
    </div>
{/if}
